import { Grid, Divider, Stack, Pagination } from "@mui/material";
import { TitledSection } from "components/Classifiers/Common/TitledSection";
import { AnswerColor } from "components/UI/AcxAnswerSelector";
import React, { useState } from "react";
import { ErrorResult } from "./ErrorResult";
import { TestLLMClassifierOnConversationResult } from "models/LLMs/Testing/TestLLMClassifierOnConversationResult";
import { ResultBadge } from "../ResultBadge";
import { getFriendlyId } from "../utils";
import { LLMClassifierBuilderStore } from "components/Classifiers/LLMClassifiers/Stores/LLMClassifierBuilderStore";

interface ErrorResultsSectionProps {
    results: TestLLMClassifierOnConversationResult[];
}

export function ErrorResultsSection({ results }: ErrorResultsSectionProps) {
    const [pageNumber, setPageNumber] = useState(1);

    if (results.length === 0) return null;

    const start =
        (pageNumber - 1) * LLMClassifierBuilderStore.TestingPerSectionPageSize;
    const end = Math.min(
        start + LLMClassifierBuilderStore.TestingPerSectionPageSize,
        results.length,
    );
    const resultsToShow = results.slice(start, end);

    const pageCount = Math.ceil(
        results.length / LLMClassifierBuilderStore.TestingPerSectionPageSize,
    );

    const sectionId = getFriendlyId("errors");

    const hasMoreThanOnePage = pageCount > 1;

    return (
        <TitledSection
            id={sectionId}
            title={
                <Stack direction="row" columnGap={2} alignItems="center">
                    <ResultBadge color={AnswerColor.RED}>
                        Errors (
                        {hasMoreThanOnePage && `${start + 1}-${end} of `}
                        {results.length})
                    </ResultBadge>
                    {hasMoreThanOnePage && (
                        <Pagination
                            count={pageCount}
                            page={pageNumber}
                            onChange={(_, value) => setPageNumber(value)}
                            size="small"
                            showFirstButton
                            showLastButton
                        />
                    )}
                </Stack>
            }
            variant="bordered"
            collapsible
            sx={(theme) => ({
                borderLeft: `4px solid ${theme.palette.red.main}`,
            })}
        >
            <Grid container direction="column" flexWrap="nowrap" rowGap={2}>
                {resultsToShow.map((result, number) => (
                    <React.Fragment key={result.conversation.id + number}>
                        <ErrorResult result={result} number={start + number} />
                        {number !== resultsToShow.length - 1 && <Divider />}
                    </React.Fragment>
                ))}
            </Grid>
            {hasMoreThanOnePage && (
                <Pagination
                    count={pageCount}
                    page={pageNumber}
                    onChange={(_, value) => {
                        setPageNumber(value);
                        document
                            .querySelector(`#${sectionId}`)
                            ?.scrollIntoView({
                                behavior: "smooth",
                            });
                    }}
                    size="small"
                />
            )}
        </TitledSection>
    );
}
